import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { gql, useQuery, useMutation } from '@apollo/client'
import { Box, Button, IconButton, InputBase, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { ConfirmDialog } from '../UI/RowActionMenu'
import { getRefundOrder, addOrderRefund, deleteOrderRefund } from '../../apollo'
import { BRAND, BRAND_DARK } from './OrderDetailView'

const GET_REFUND_ORDER = gql`
  ${getRefundOrder}
`
const ADD_REFUND = gql`
  ${addOrderRefund}
`
const DELETE_REFUND = gql`
  ${deleteOrderRefund}
`

const LINE = '#EEF0F4'
const MUTED = '#6B7280'

// Only Borku admins/staff record refunds (the server enforces the same);
// vendors see their store's orders through this modal too.
export const canRecordRefunds = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user-borku') || 'null')
    return ['ADMIN', 'STAFF'].includes(user?.userType)
  } catch (e) {
    return false
  }
}

const formatDate = value =>
  value ? new Date(value).toLocaleDateString('en-GB') : ''

/**
 * Partial refunds for one order, recorded from the order modal. Same records
 * as Reporting → Partial refunds: they net off this order in the store report.
 * Nothing here moves money — the refund is issued in Stripe first.
 */
const OrderRefunds = ({ orderId, currency = '£' }) => {
  const { t } = useTranslation()
  const money = v => `${currency}${Number(v || 0).toFixed(2)}`
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [message, setMessage] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const { data, loading } = useQuery(GET_REFUND_ORDER, {
    variables: { orderId },
    skip: !orderId,
    fetchPolicy: 'network-only'
  })
  const order = data?.refundOrder

  // Keep the Reporting tab's refund list in step if it is mounted
  const refetchQueries = ['PartialRefunds']

  const [saveRefund, { loading: saving }] = useMutation(ADD_REFUND, {
    refetchQueries,
    update: (cache, { data: result }) =>
      cache.writeQuery({
        query: GET_REFUND_ORDER,
        variables: { orderId },
        data: { refundOrder: result.addOrderRefund }
      }),
    onCompleted: () => {
      setMessage({ error: false, text: t('RefundRecorded') })
      setAmount('')
      setReason('')
    },
    onError: error => setMessage({ error: true, text: error.message })
  })
  const [removeRefund, { loading: removing }] = useMutation(DELETE_REFUND, {
    refetchQueries,
    update: (cache, { data: result }) =>
      cache.writeQuery({
        query: GET_REFUND_ORDER,
        variables: { orderId },
        data: { refundOrder: result.deleteOrderRefund }
      }),
    onCompleted: () => setDeleteTarget(null),
    onError: error => {
      setDeleteTarget(null)
      setMessage({ error: true, text: error.message })
    }
  })

  if (loading || !order) return null

  const refunded = order.refundedAmount || 0
  const remaining = Math.max((order.orderAmount || 0) - refunded, 0)
  const parsedAmount = Number(amount)
  const canSave =
    parsedAmount > 0 && parsedAmount <= remaining + 0.001 && !saving

  const onSave = () => {
    if (!canSave) return
    setMessage(null)
    saveRefund({
      variables: { orderId: order.orderId, amount: parsedAmount, reason }
    })
  }

  const inputSx = {
    height: 42,
    px: 1.75,
    fontSize: 14,
    borderRadius: '10px',
    border: '1px solid #E5E7EB',
    bgcolor: '#FFFFFF',
    '&.Mui-focused': { borderColor: BRAND }
  }

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: 2,
        bgcolor: '#FFFFFF',
        borderTop: `1px solid ${LINE}`
      }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 1,
          mb: 1.5
        }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
          {t('PartialRefunds')}
        </Typography>
        <Typography sx={{ fontSize: 13, color: MUTED }}>
          {`${t('Refunded')} ${money(refunded)} · ${t('NetPaid')} ${money(
            remaining
          )}`}
        </Typography>
      </Box>

      {order.refunds.map(refund => (
        <Box
          key={refund._id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1,
            borderBottom: `1px solid ${LINE}`
          }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14 }}>
              {refund.reason || t('Refund')}
            </Typography>
            <Typography sx={{ fontSize: 12, color: MUTED }}>
              {[refund.createdBy, formatDate(refund.createdAt)]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {`-${money(refund.amount)}`}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('RemoveRefund')}
            onClick={() =>
              setDeleteTarget({ orderId: order.orderId, refundId: refund._id })
            }>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      {remaining > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.25,
            mt: 1.5
          }}>
          <InputBase
            type="number"
            placeholder={`${t('RefundAmount')} (max ${money(remaining)})`}
            inputProps={{ min: 0, max: remaining, step: '0.01' }}
            value={amount}
            onChange={event => setAmount(event.target.value)}
            sx={{ ...inputSx, flex: '0 1 220px' }}
          />
          <InputBase
            placeholder={t('RefundReasonPlaceholder')}
            value={reason}
            onChange={event => setReason(event.target.value)}
            sx={{ ...inputSx, flex: '1 1 200px' }}
          />
          <Button
            variant="contained"
            disableElevation
            disabled={!canSave}
            onClick={onSave}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '999px',
              px: 3,
              height: 42,
              bgcolor: BRAND,
              '&:hover': { bgcolor: BRAND_DARK }
            }}>
            {t('RecordRefund')}
          </Button>
        </Box>
      ) : null}

      <Typography sx={{ fontSize: 12, color: MUTED, mt: 1 }}>
        {t('PartialRefundsHelp')}
      </Typography>
      {message && (
        <Typography
          sx={{
            mt: 1,
            fontSize: 13,
            color: message.error ? 'error.main' : 'success.main'
          }}>
          {message.text}
        </Typography>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`${t('RemoveRefund')}?`}
        message={t('RemoveRefundConfirm')}
        confirmLabel={t('RemoveRefund')}
        cancelLabel={t('Cancel')}
        loading={removing}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => removeRefund({ variables: deleteTarget })}
      />
    </Box>
  )
}

export default OrderRefunds
